import { useCallback, useEffect, useState } from "react";
import { LeaveFormData, LeaveType, TourPlanFormData, TourPlanType } from "../types/tourPlan";
import { addLeave, addTourPlan, addTourPlanDeviation, getAllLeaves, getAllTourPlans, getLocationsByEmployee, updateLeave as updateLeaveApi, updateTourPlanDeviation as updateTourPlanDeviationApi } from "../api/tour-plan";


export const useWorkCalendar = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    const [tourPlans, setTourPlans] = useState<TourPlanType[]>([]);
    const [leaves, setLeaves] = useState<LeaveType[]>([]);


    const resetState = () => {
        setError(null);
        setSuccess(null);
    };

    // -----------------------
    // Fetch Locations
    // -----------------------
    const fetchLocations = async (employeeId: number) => {
        try {
            resetState();
            const res = await getLocationsByEmployee(employeeId);
            return res.data.data;
        } catch {
            setError("Failed to fetch locations");
            throw new Error();
        }
    };



    // -----------------------
    // Fetch Tour Plans
    // -----------------------
    const fetchTourPlans = useCallback(
        async (employeeId: number, tenant_id: number) => {
            try {
                setLoading(true);
                resetState();

                const res = await getAllTourPlans(employeeId, tenant_id);
                setTourPlans(res.data.data);

                return res.data.data as TourPlanType[];
            } catch (error: any) {
                setError("Failed to fetch tour plans");
                console.log("error calendar: ", error.response?.data.message);
                throw new Error();
            } finally {
                setLoading(false);
            }
        },
        [] // 👈 safe: uses only setState + imported API
    );

    // -----------------------
    // Fetch Leaves
    // -----------------------
    const fetchLeaves = async (employeeId: number, tenantId: number) => {
        try {
            setLoading(true);
            resetState();

            const res = await getAllLeaves(employeeId, tenantId);
            setLeaves(res.data.data);

            return res.data.data as LeaveType[];
        } catch {
            setError("Failed to fetch leaves");
            throw new Error();
        } finally {
            setLoading(false);
        }
    };

    // -----------------------
    // Submit Tour Plan
    // -----------------------
    const submitTourPlan = async (payload: TourPlanFormData[]) => {
        try {
            setLoading(true);
            setError(null);
            console.log(payload);
            await addTourPlan(payload);
            setSuccess("Tour plan saved successfully");

            return { ok: true };

        } catch (err: any) {
            const message =
                err?.response?.data?.message ||
                err?.response?.data?.error ||
                "Failed to save tour plan";

            setError(message);
            return { ok: false, error: message };

        } finally {
            setLoading(false);
        }
    };

    const createTourPlanDeviation = async (tenantId: number, employeeId: number, tourPlanId: number, locationId: number, remarks: string) => {
        try {
            setLoading(true);
            setError(null);
            const response = await addTourPlanDeviation(tenantId, employeeId, tourPlanId, locationId, remarks);
            if (response.status === 200) {
                return { ok: true }
            } else {
                return { ok: false, message: response.data.data.message };
            }
        } catch (error: any) {
            const message = error?.response.data.message;
            console.error(message);
            return { ok: false, message: message }
        }
    }


    // -----------------------
    // Submit Leave
    // -----------------------
    const submitLeave = async (payload: LeaveFormData[]) => {
        try {
            setLoading(true);
            resetState();

            await addLeave(payload);
            setSuccess("Leave submitted successfully");
            return true;
        } catch {
            setError("Failed to submit leave");
            return false;
        } finally {
            setLoading(false);
        }
    };

    // -----------------------
    // Update Tour Plan
    // -----------------------
    const updateTourPlan = async (payload: TourPlanType[]) => {
        try {
            setLoading(true);
            resetState();

            await updateTourPlan(payload);
            setSuccess("Tour plan updated successfully");
            return true;
        } catch {
            setError("Failed to update tour plan");
            return false;
        } finally {
            setLoading(false);
        }
    };

    // -----------------------
    // Update Tour Plan Deviation
    // -----------------------
    const updateTourPlanDeviation = async (tenant_id: number, tour_plan_id: number, deviation_id: number, status: string, approved_by: number) => {
        try {
            setLoading(true);
            resetState();
            const res = await updateTourPlanDeviationApi(tenant_id, tour_plan_id, deviation_id, status, approved_by);
            console.log("deviation update res: ", res.data.data)
            setSuccess("Tour plan Deviation updated successfully");
            return true;
        } catch (error: any) {
            const message =
                error?.res?.data?.message ||
                error?.res?.data?.error;
            console.log("deviation error: ", message)
            setError("Failed to update tour plan deviation");
            return false;
        } finally {
            setLoading(false);
        }
    };

    // -----------------------
    // Update Leave
    // -----------------------
    const updateLeave = async (payload: LeaveType[]) => {
        try {
            setLoading(true);
            resetState();

            await updateLeaveApi(payload);
            setSuccess("Leave updated successfully");
            return true;
        } catch {
            setError("Failed to update leave");
            return false;
        } finally {
            setLoading(false);
        }
    };



    return {
        loading,
        error,
        success,

        tourPlans,
        leaves,

        fetchLocations,
        fetchTourPlans,
        fetchLeaves,

        submitTourPlan,
        createTourPlanDeviation,
        submitLeave,
        updateTourPlan,
        updateLeave,
        updateTourPlanDeviation,
        resetState,
    };
};
